import { useEffect, useMemo, useState } from 'react';

import { App, Button, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Layout from 'components/Layout/Layout';

import { ApiErrorData } from 'models/ApiResponse';
import {
  OperationBriefForm,
  OperationRequestDraftResult,
  OperationRequestReceipt,
} from 'models/OperationRequest';
import { Store } from 'models/Store';

import { OperationRequirementService } from 'services/OperationRequirementService';
import { StoreService } from 'services/StoreService';

import OpsBriefForm from './components/OpsBriefForm';
import OpsBriefSummary from './components/OpsBriefSummary';
import OpsCreateFooter from './components/OpsCreateFooter';
import OpsReceiptModal, {
  OpsReceiptModalMode,
} from './components/OpsReceiptModal';
import OpsReqStepper, { NewReqStep } from './components/OpsReqStepper';
import OpsWeekPlanner, {
  WeekPlanCells,
  computePlanMetrics,
} from './components/OpsWeekPlanner';
import { buildOperationRequestPayload } from './data/buildOperationRequestPayload';
import { OPS_DEFAULT_BRIEF } from './data/operationCatalogs';

import './NewOperationRequest.scss';

function isBriefReady(brief: OperationBriefForm) {
  return !!(
    brief.client?.trim() &&
    brief.campaign?.trim() &&
    brief.week &&
    brief.profile &&
    brief.kam &&
    brief.rate >= 0
  );
}

function NewOperationRequest() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const i18nBase =
    'scenes.requirements_operation.components.new_operation_request';

  const draftIdParam = searchParams.get('draftId');
  const editIdParam = searchParams.get('editId');
  const isEditMode = !!editIdParam;

  const [step, setStep] = useState<NewReqStep>('brief');
  const [brief, setBrief] = useState<OperationBriefForm>({
    ...OPS_DEFAULT_BRIEF,
    blocks: {
      am: { ...OPS_DEFAULT_BRIEF.blocks.am },
      pm: { ...OPS_DEFAULT_BRIEF.blocks.pm },
      full: { ...OPS_DEFAULT_BRIEF.blocks.full },
    },
  });
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [cells, setCells] = useState<WeekPlanCells>({});
  const [stores, setStores] = useState<Store[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [loadingRequest, setLoadingRequest] = useState(
    !!draftIdParam || !!editIdParam
  );
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<OperationRequestReceipt | null>(null);
  const [receiptMode, setReceiptMode] = useState<OpsReceiptModalMode | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await StoreService.list({
        filter: 'ALL',
        page: 1,
        pageSize: 100,
      });
      if (cancelled) return;
      if (response.success) {
        const data = response.data as { items: Store[] };
        setStores(data.items.filter((s) => s.isActive));
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.fetch_stores`),
          3.5
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [message, t, i18nBase]);

  useEffect(() => {
    const id = editIdParam || draftIdParam;
    if (!id) {
      setLoadingRequest(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoadingRequest(true);
      const response = await OperationRequirementService.getDraft(id);
      if (cancelled) return;
      setLoadingRequest(false);

      if (!response.success) {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(`${i18nBase}.errors.${editIdParam ? 'load_edit' : 'draft'}`),
          3.5
        );
        return;
      }

      const loaded = response.data as OperationRequestDraftResult;
      if (
        editIdParam &&
        (loaded.requestStatus === 'publicado' ||
          loaded.requestStatus === 'cancelado')
      ) {
        message.warning(
          t(`${i18nBase}.errors.locked_${loaded.requestStatus}`, {
            id: loaded.id,
          }),
          3.5
        );
        navigate('/portal-go/requirements-review', { replace: true });
        return;
      }
      const { brief: loadedBrief, plan } = loaded.payload;

      if (editIdParam) {
        setEditId(loaded.id);
        setDraftId(null);
      } else {
        setDraftId(loaded.id);
        setEditId(null);
      }

      setBrief({
        ...loadedBrief,
        blocks: {
          am: { ...loadedBrief.blocks.am },
          pm: { ...loadedBrief.blocks.pm },
          full: { ...loadedBrief.blocks.full },
        },
      });
      setSelectedStoreIds([...plan.storeIds]);
      setCells({ ...plan.cells });
      if (plan.storeIds.length > 0 || plan.turns.length > 0) {
        setStep('turns');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draftIdParam, editIdParam, message, t, i18nBase, navigate]);

  const metrics = useMemo(
    () => computePlanMetrics(selectedStoreIds, cells, brief),
    [selectedStoreIds, cells, brief]
  );

  const briefReady = isBriefReady(brief);
  const canSubmit = metrics.turnos > 0;
  const canSaveDraft = !!(brief.client?.trim() || brief.campaign?.trim());

  const goBack = () => navigate('/portal-go/requirements-review');

  const handleDraft = async () => {
    if (isEditMode) return;
    if (!canSaveDraft) {
      message.warning(t(`${i18nBase}.messages.draft_empty`), 3.5);
      return;
    }

    const payload = buildOperationRequestPayload({
      status: 'draft',
      brief,
      selectedStoreIds,
      cells,
    });

    setSavingDraft(true);
    const response = await OperationRequirementService.saveDraft({
      payload,
      draftId,
    });
    setSavingDraft(false);

    if (response.success) {
      const data = response.data as { id: string };
      setDraftId(data.id);
      message.success(
        t(`${i18nBase}.messages.draft_saved`, { id: data.id }),
        3.5
      );
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.draft`),
        3.5
      );
    }
  };

  const handleContinue = () => {
    if (!briefReady) {
      message.warning(t(`${i18nBase}.messages.brief_incomplete`), 3.5);
      return;
    }
    setStep('turns');
  };

  const buildPreviewReceipt = (): OperationRequestReceipt => {
    const vipStores = selectedStoreIds.filter(
      (id) => stores.find((s) => s.id === id)?.isVip
    ).length;

    return {
      id: editId || 'PREVIEW',
      brand: brief.client.trim(),
      type: brief.type,
      campaign: brief.campaign.trim(),
      week: brief.week,
      profile: brief.profile,
      kam: brief.kam,
      turnos: metrics.turnos,
      slots: metrics.people,
      stores: selectedStoreIds.length,
      vipStores,
      cost: metrics.cost,
      email: {
        to: '',
        cc: '',
        subject: '',
        body: '',
      },
    };
  };

  /** Abre el modal de revisión; aún no guarda. */
  const handleReadyToSend = () => {
    if (!canSubmit) {
      message.warning(t(`${i18nBase}.messages.plan_incomplete`), 3.5);
      return;
    }
    setReceipt(buildPreviewReceipt());
    setReceiptMode('preview');
  };

  /** Confirma desde el modal: crea o actualiza. */
  const handleConfirmSend = async () => {
    const payload = buildOperationRequestPayload({
      status: 'submitted',
      brief,
      selectedStoreIds,
      cells,
    });

    setSubmitting(true);
    const response = editId
      ? await OperationRequirementService.update(editId, { payload })
      : await OperationRequirementService.create({
          brief: payload.brief,
          turns: payload.plan.turns,
          payload,
          draftId,
        });
    setSubmitting(false);

    if (response.success) {
      setDraftId(null);
      const data = response.data as {
        receipt: OperationRequestReceipt;
      };
      setReceipt(data.receipt);
      setReceiptMode('success');
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(`${i18nBase}.errors.${editId ? 'update' : 'submit'}`),
        3.5
      );
    }
  };

  const closeReceiptModal = () => {
    if (submitting) return;
    setReceipt(null);
    setReceiptMode(null);
  };

  return (
    <Layout title="">
      <div className="new-operation-request">
        <div className="new-operation-request__header">
          <div>
            <h1 className="new-operation-request__title">
              {t(`${i18nBase}.${isEditMode ? 'edit_title' : 'title'}`)}
            </h1>
          </div>
          <Button onClick={goBack}>{t(`${i18nBase}.buttons.back`)}</Button>
        </div>

        {loadingRequest ? (
          <div className="new-operation-request__loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            <OpsReqStepper
              step={step}
              briefDone={briefReady}
              onChange={setStep}
            />

            {step === 'brief' ? (
              <OpsBriefForm value={brief} onChange={setBrief} />
            ) : (
              <>
                <OpsBriefSummary
                  brief={brief}
                  onEdit={() => setStep('brief')}
                />
                <OpsWeekPlanner
                  brief={brief}
                  stores={stores}
                  selectedStoreIds={selectedStoreIds}
                  cells={cells}
                  onSelectedStoresChange={setSelectedStoreIds}
                  onCellsChange={setCells}
                />
              </>
            )}

            <OpsCreateFooter
              mode={step}
              variant={isEditMode ? 'edit' : 'create'}
              metrics={metrics}
              savingDraft={savingDraft}
              submitting={false}
              canContinue={briefReady}
              canSubmit={canSubmit}
              onDraft={handleDraft}
              onContinue={handleContinue}
              onSubmit={handleReadyToSend}
            />
          </>
        )}
      </div>

      <OpsReceiptModal
        receipt={receipt}
        mode={receiptMode}
        submitting={submitting}
        variant={isEditMode ? 'edit' : 'create'}
        onClose={closeReceiptModal}
        onConfirm={handleConfirmSend}
        onDone={() => {
          closeReceiptModal();
          goBack();
        }}
      />
    </Layout>
  );
}

export default NewOperationRequest;
